import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Warning, CheckCircle, Clock, Certificate, Users, FileText,
  CaretRight, X, FunnelSimple, MagnifyingGlass, Sparkle, ArrowsClockwise
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Alert severity configurations
const SEVERITY_CONFIG = {
  high: {
    color: '#E5484D',
    bg: '#3A1214',
    border: '#5C1D1F',
    icon: <Warning size={20} weight="fill" />
  },
  medium: {
    color: '#F5A623',
    bg: '#3E2A0A',
    border: '#634310',
    icon: <Clock size={20} weight="fill" />
  },
  low: {
    color: '#30A46C',
    bg: '#0F2E1B',
    border: '#174529',
    icon: <CheckCircle size={20} weight="fill" />
  }
};

// Alert type icons
const ALERT_TYPE_ICONS = {
  licence_renewal: <Certificate size={20} />,
  appointment_expiry: <Users size={20} />,
  filing_due: <FileText size={20} />,
  missing_doc: <FileText size={20} />,
  draft_law_update: <Bell size={20} />,
  policy_review: <FileText size={20} />,
  regulatory_impact: <Warning size={20} />
};

const SeverityBadge = ({ severity }) => {
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.low;
  return (
    <span 
      className="px-2.5 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider border flex items-center gap-1.5"
      style={{ 
        color: config.color, 
        backgroundColor: config.bg, 
        borderColor: config.border 
      }}
    >
      {config.icon}
      {severity}
    </span>
  );
};

const AlertCard = ({ alert, onAcknowledge, onDismiss }) => {
  const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.low;
  const typeIcon = ALERT_TYPE_ICONS[alert.alert_type] || <Bell size={20} />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`p-4 bg-background-surface border rounded-sm transition-all hover:border-gold/50 ${
        alert.acknowledged ? 'opacity-60' : ''
      }`}
      style={{ borderColor: alert.acknowledged ? '#2D2D2D' : config.border }}
      data-testid={`alert-card-${alert.id}`}
    >
      <div className="flex items-start gap-4">
        <div 
          className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: config.bg, color: config.color }}
        >
          {typeIcon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="text-sm font-medium text-text-primary">
              {alert.title}
            </h3>
            <SeverityBadge severity={alert.severity} />
          </div>
          <p className="text-sm text-text-secondary mb-3">
            {alert.message}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-text-tertiary">
              {alert.due_date && (
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  Due: {new Date(alert.due_date).toLocaleDateString()}
                </span>
              )}
              <span>
                {new Date(alert.created_at).toLocaleDateString()}
              </span>
            </div>
            {!alert.acknowledged && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onAcknowledge(alert.id)}
                  className="px-3 py-1.5 bg-gold/10 hover:bg-gold/20 text-gold text-xs font-medium rounded-sm transition-all"
                  data-testid={`acknowledge-alert-${alert.id}`}
                >
                  Acknowledge
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ComplianceAlertsPage = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/compliance-alerts`);
      setAlerts(response.data);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAlerts = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/compliance-alerts/generate`);
      toast.success(response.data.message);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to generate alerts');
    } finally {
      setGenerating(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.put(`${API}/compliance-alerts/${alertId}/acknowledge`);
      setAlerts(alerts.map(a => 
        a.id === alertId ? { ...a, acknowledged: true } : a
      ));
      toast.success('Alert acknowledged');
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  // Filtered alerts
  const filteredAlerts = useMemo(() => {
    let result = [...alerts];
    
    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(alert => 
        alert.title.toLowerCase().includes(q) ||
        alert.message.toLowerCase().includes(q)
      );
    }
    
    // Severity filter
    if (severityFilter !== 'all') {
      result = result.filter(alert => alert.severity === severityFilter);
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(alert => alert.alert_type === typeFilter);
    }
    
    // Acknowledged filter
    if (!showAcknowledged) {
      result = result.filter(alert => !alert.acknowledged);
    }
    
    // Sort by severity and date
    result.sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });
    
    return result;
  }, [alerts, searchQuery, severityFilter, typeFilter, showAcknowledged]);

  // Stats
  const stats = useMemo(() => {
    const unacknowledged = alerts.filter(a => !a.acknowledged);
    return {
      total: unacknowledged.length,
      high: unacknowledged.filter(a => a.severity === 'high').length,
      medium: unacknowledged.filter(a => a.severity === 'medium').length,
      low: unacknowledged.filter(a => a.severity === 'low').length
    };
  }, [alerts]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="text-gold font-mono">Loading compliance alerts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary mb-1">
                Compliance Alerts
              </h1>
              <p className="text-sm text-text-secondary">
                Monitor license renewals, appointment expirations, and compliance deadlines
              </p>
            </div>
            <button
              onClick={handleGenerateAlerts}
              disabled={generating}
              className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all flex items-center gap-2 disabled:opacity-50"
              data-testid="generate-alerts-btn"
            >
              {generating ? (
                <>
                  <ArrowsClockwise size={16} className="animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Sparkle size={16} />
                  Scan for Alerts
                </>
              )}
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-gold">{stats.total}</div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                Active Alerts
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-4 bg-risk-high-bg border border-risk-high-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-risk-high-text">{stats.high}</div>
              <div className="text-xs uppercase tracking-wider text-risk-high-text opacity-80 mt-1">
                High Priority
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-4 bg-risk-medium-bg border border-risk-medium-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-risk-medium-text">{stats.medium}</div>
              <div className="text-xs uppercase tracking-wider text-risk-medium-text opacity-80 mt-1">
                Medium Priority
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-risk-low-bg border border-risk-low-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-risk-low-text">{stats.low}</div>
              <div className="text-xs uppercase tracking-wider text-risk-low-text opacity-80 mt-1">
                Low Priority
              </div>
            </motion.div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search alerts..."
                className="w-full pl-10 pr-4 py-2.5 bg-background-surface border border-border rounded-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
                data-testid="search-alerts"
              />
            </div>
            <div className="flex items-center gap-2">
              <FunnelSimple size={18} className="text-text-tertiary" />
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="px-3 py-2.5 bg-background-surface border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none text-sm"
                data-testid="filter-severity"
              >
                <option value="all">All Severity</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2.5 bg-background-surface border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none text-sm"
                data-testid="filter-type"
              >
                <option value="all">All Types</option>
                <option value="licence_renewal">License Renewal</option>
                <option value="appointment_expiry">Appointment Expiry</option>
                <option value="policy_review">Policy Review</option>
                <option value="regulatory_impact">Regulatory Impact</option>
                <option value="filing_due">Filing Due</option>
                <option value="missing_doc">Missing Document</option>
              </select>
              <label className="flex items-center gap-2 px-3 py-2.5 bg-background-surface border border-border rounded-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAcknowledged}
                  onChange={(e) => setShowAcknowledged(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-gold focus:ring-gold"
                />
                <span className="text-sm text-text-primary">Show acknowledged</span>
              </label>
            </div>
          </div>
        </div>

        {/* Alerts List */}
        <div className="p-6">
          {filteredAlerts.length > 0 ? (
            <div className="space-y-4">
              <AnimatePresence>
                {filteredAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={handleAcknowledge}
                  />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="text-center py-16 bg-background-surface border border-border rounded-sm">
              <div className="w-16 h-16 bg-risk-low-bg border border-risk-low-border rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-risk-low-text" weight="light" />
              </div>
              <h3 className="text-lg font-heading font-medium text-text-primary mb-2">
                {searchQuery || severityFilter !== 'all' || typeFilter !== 'all'
                  ? 'No Matching Alerts'
                  : 'All Clear'}
              </h3>
              <p className="text-sm text-text-secondary mb-6">
                {searchQuery || severityFilter !== 'all' || typeFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'No active compliance alerts. Click "Scan for Alerts" to check for upcoming deadlines.'}
              </p>
              {!searchQuery && severityFilter === 'all' && typeFilter === 'all' && (
                <button
                  onClick={handleGenerateAlerts}
                  disabled={generating}
                  className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all"
                >
                  Scan for Alerts
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceAlertsPage;
